import { Link } from "react-router-dom";
import { ArrowRight, Music2 } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";

const SONGS = [
  { id: "father-me", title: "Father Me", src: "/audio/father-me-sample.mp3", cover: "/images/covers/father-me.jpg" },
  {
    id: "feel-the-nail",
    title: "Feel The Nail",
    src: "/audio/feel-the-nail-sample.mp3",
    cover: "/images/covers/feel-the-nail.jpg",
  },
  {
    id: "lord-i-lift-your-name-on-high",
    title: "Lord I Lift Your Name On High",
    src: "/audio/lord-i-lift-your-name-on-high-sample.mp3",
    cover: "/images/covers/lord-i-lift-your-name-on-high.jpg",
  },
  {
    id: "longtime-friend",
    title: "Longtime Friend",
    src: "/audio/longtime-friend-sample.mp3",
    cover: "/images/covers/longtime-friend.jpg",
  },
];

const coverClass =
  "aspect-square w-40 shrink-0 self-center border border-gold/20 object-cover sm:w-44 sm:self-auto";

const Cover = ({ song }) =>
  song.cover ? (
    <img src={song.cover} alt={`${song.title} — cover artwork`} data-testid={`cover-${song.id}`} className={coverClass} />
  ) : (
    <div
      data-testid={`cover-${song.id}`}
      className={`flex flex-col items-center justify-center gap-3 bg-charcoal-850 ${coverClass}`}
      aria-label={`Cover artwork placeholder for ${song.title}`}
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-gold">
        <Music2 size={20} strokeWidth={1.5} />
      </span>
      <span className="font-serif text-2xl font-light italic text-gold-light/80">{song.title.charAt(0)}</span>
    </div>
  );

const SongCard = ({ song, index }) => (
  <Reveal delay={0.08 * index}>
    <article
      data-testid={`song-${song.id}`}
      className="border border-white/[0.08] bg-charcoal-900/60 p-6 transition-colors duration-300 hover:border-gold/40 sm:p-8"
    >
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
        <Cover song={song} />
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
            Christian &amp; Inspirational Recording
          </p>
          <h2 className="mt-2 font-serif text-xl sm:text-2xl font-light text-sand-bright">{song.title}</h2>
          <p className="mt-2 text-sm font-light text-mist">Listening sample — streaming only</p>
        </div>
      </div>
      <audio
        controls
        controlsList="nodownload"
        preload="none"
        src={song.src}
        onContextMenu={(e) => e.preventDefault()}
        className="mt-6 w-full"
        data-testid={`audio-player-${song.id}`}
      />
      <div className="mt-6 border-t border-white/[0.06] pt-5">
        <Link
          to="/contact"
          data-testid={`request-purchase-${song.id}`}
          className="group inline-flex items-center gap-2 border border-gold/50 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-charcoal-950"
        >
          Request to Purchase
          <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </article>
  </Reveal>
);

export default function Music() {
  return (
    <>
      <Seo
        title="Music — Derrick Hall"
        siteName="Derrick Hall"
        description="Listen to selected Christian and inspirational recordings by Derrick Hall — streaming listening samples."
      />
      <PageHeader
        testId="music-header"
        eyebrow="Music"
        title="Recordings"
        description="Listen to selected Christian and inspirational recordings by Derrick Hall. Each song features a listening sample, cover artwork, and a brief note about the music. Full recordings are not available for direct download."
      />
      <div className="mx-auto max-w-5xl space-y-6 px-6 pb-28" data-testid="music-track-list">
        {SONGS.map((s, i) => (
          <SongCard key={s.id} song={s} index={i} />
        ))}
      </div>
    </>
  );
}
